/**
 * A card's soft colour, derived from a stable seed (a frame or project id, a
 * link's domain): one of the six `--card-tint-N-a/b` pairs in index.css, so
 * the same item always wears the same colour and each theme tunes the pairs.
 */
import type { CSSProperties } from "react";

const TINT_COUNT = 6;

/** 1–6: the tint pair `seed` wears (FNV-1a over its characters). */
export function cardTintIndex(seed: string): number {
  let hash = 0x811c9dc5;
  for (let index = 0; index < seed.length; index += 1) {
    hash ^= seed.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return ((hash >>> 0) % TINT_COUNT) + 1;
}

/** `--tint-a` / `--tint-b` for `seed`, read by the card's gradient classes. */
export function cardTintStyle(seed: string): CSSProperties {
  const tint = cardTintIndex(seed);
  return {
    "--tint-a": `var(--card-tint-${tint}-a)`,
    "--tint-b": `var(--card-tint-${tint}-b)`,
  } as CSSProperties;
}

const HEX = /^#([0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;
const RGB =
  /^rgba?\(\s*([\d.]+)%?[\s,]+([\d.]+)%?[\s,]+([\d.]+)%?(?:[\s,/]+([\d.]+)(%?))?\s*\)$/i;
/** Below this opacity a colour shows what is behind it, not itself. */
const MIN_ALPHA = 0.5;

/** 0–1 opacity of a hex colour's alpha digits ("" is opaque). */
function hexAlpha(digits: string): number {
  if (!digits) return 1;
  const wide = digits.length === 1 ? digits + digits : digits;
  return parseInt(wide, 16) / 255;
}

/** The RGB channels of a mostly opaque hex or `rgb()` colour, else null. */
function channels(color: string): [number, number, number] | null {
  const hex = HEX.exec(color.trim());
  if (hex) {
    const digits = hex[1]!;
    const short = digits.length <= 4;
    const wide = short
      ? [...digits.slice(0, 3)].map((digit) => digit + digit).join("")
      : digits.slice(0, 6);
    if (hexAlpha(digits.slice(short ? 3 : 6)) < MIN_ALPHA) return null;
    return [0, 2, 4].map((at) => parseInt(wide.slice(at, at + 2), 16)) as [
      number,
      number,
      number,
    ];
  }
  const rgb = RGB.exec(color.trim());
  if (!rgb) return null;
  const alpha = rgb[4] === undefined ? 1 : Number(rgb[4]) / (rgb[5] ? 100 : 1);
  if (alpha < MIN_ALPHA) return null;
  const values = [Number(rgb[1]), Number(rgb[2]), Number(rgb[3])];
  return values.every(Number.isFinite)
    ? (values as [number, number, number])
    : null;
}

/**
 * `value` when it is a plain, mostly opaque hex or `rgb()` colour (content
 * data), else null.
 */
export function plainColor(value: unknown): string | null {
  return typeof value === "string" && channels(value) ? value.trim() : null;
}

/** A plain, mostly opaque colour as lowercase `#rrggbb`, else null. */
export function hexColor(value: unknown): string | null {
  const rgb = typeof value === "string" ? channels(value) : null;
  if (!rgb) return null;
  return `#${rgb
    .map((channel) =>
      Math.round(Math.min(255, Math.max(0, channel)))
        .toString(16)
        .padStart(2, "0"),
    )
    .join("")}`;
}

/** Channel spread at which a colour reads as a brand colour, not a grey. */
const VIVID_SPREAD = 40;

/** True when `color` is a plain colour with a hue (not white, black or grey). */
export function isVividColor(color: string): boolean {
  const rgb = channels(color);
  return rgb !== null && Math.max(...rgb) - Math.min(...rgb) >= VIVID_SPREAD;
}
